import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, Code, Settings, BarChart3, Network, FileCheck } from "lucide-react";
import { LANGUAGES_SIMPLE } from "@/lib/languages";
import { useSignIn } from "@/hooks/useSignIn";

// Supported languages - matching what's shown in My Content tab article settings
const POPULAR_LANGUAGE_CODES = [
  // Major European Languages
  'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'no', 'da', 'fi', 'cs', 'hu', 'ro', 'bg', 'hr', 'sk', 'sl', 'tr', 'uk', 'el', 'lt', 'lv', 'et', 'mk', 'sq', 'sr', 'bs', 'me', 'ca', 'eu', 'gl', 'cy', 'ga', 'gd', 'mt', 'is', 'fo', 'lb',
  // Asian Languages
  'ja', 'ko', 'zh', 'zh-tw', 'hi', 'bn', 'ur', 'ta', 'te', 'ml', 'kn', 'gu', 'pa', 'th', 'vi', 'id', 'ms', 'tl', 'my', 'km', 'lo',
  // Middle Eastern & African Languages
  'ar', 'fa', 'he', 'am', 'sw', 'ha', 'yo', 'ig', 'zu', 'af', 'xh',
  // Americas Regional Variants
  'pt-br', 'es-mx', 'es-ar', 'fr-ca'
];

// Get only the supported languages (excluding 'ru' and 'be')
const SUPPORTED_LANGUAGES = LANGUAGES_SIMPLE
  .filter(l => POPULAR_LANGUAGE_CODES.includes(l.value))
  .filter(l => l.value !== 'ru' && l.value !== 'be');

interface AdditionalFeaturesProps {
  onSignIn: () => void;
}

const AdditionalFeatures: React.FC<AdditionalFeaturesProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  const features = [
    {
      icon: <Globe className="h-6 w-6" />,
      title: `${SUPPORTED_LANGUAGES.length} Languages Supported`,
      description: "Create content that speaks to your audience in their language. Our AI writes naturally in your target language—not machine translations. Reach global markets with authentic, native-level content."
    },
    {
      icon: <Code className="h-6 w-6" />,
      title: "LLM.txt Generation",
      description: "Get discovered by AI search engines. We automatically generate LLM.txt files that help AI agents understand and index your content, boosting your visibility in AI-powered search results."
    },
    {
      icon: <Settings className="h-6 w-6" />,
      title: "Full Content Control",
      description: "Customize every detail to match your brand. Control language, writing style, word count, image style, article format, and SEO settings. Every article is tailored exactly how you want it."
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Real Performance Data",
      description: "See what's actually working. Connect your Google Search Console to track real clicks, impressions, and rankings. Make data-driven decisions with actual performance metrics, not guesses."
    },
    {
      icon: <Network className="h-6 w-6" />,
      title: "Strategic Internal Linking",
      description: "Every article automatically includes smart internal links that connect related content. Build a strong site structure that helps search engines understand your content hierarchy and keeps readers engaged."
    },
    {
      icon: <FileCheck className="h-6 w-6" />,
      title: "SEO-Optimized Structure",
      description: "Every article is built with proper heading hierarchy, meta tags, and semantic HTML. Our AI ensures your content follows SEO best practices from the ground up, so it's ready to rank."
    }
  ];

  return (
    <section className="w-full py-16 sm:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <div className="flex items-end justify-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 mb-6 sm:mb-8">
              <img
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/arrow-left-black.e540828f.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2Fycm93LWxlZnQtYmxhY2suZTU0MDgyOGYuc3ZnIiwiaWF0IjoxNzYzODUxMTk3LCJleHAiOjE5MjE1MzExOTd9.NUkKZ_BV06mSN_QC0__-ea_ZjVWlm0iEIDECHyE3lSc"
                alt=""
                className="h-5 sm:h-6 md:h-7 lg:h-8 w-auto hidden sm:block flex-shrink-0 -mb-0.5 sm:-mb-1"
                aria-hidden="true"
              />
              <div className="flex flex-col items-center">
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="text-foreground">Everything you need</span>
                  <br />
                  <span className="text-primary">to rank higher and grow faster.</span>
                </h2>
              </div>
              <img
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/arrow-right-black.538c912a.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2Fycm93LXJpZ2h0LWJsYWNrLjUzOGM5MTJhLnN2ZyIsImlhdCI6MTc2Mzg1MTIwOSwiZXhwIjoxOTIxNTMxMjA5fQ.bBnvaFY4fcPXFcUI3k4ZcGcVmjjZsJVbgvuLZpQNQXI"
                alt=""
                className="h-5 sm:h-6 md:h-7 lg:h-8 w-auto hidden sm:block flex-shrink-0 -mb-0.5 sm:-mb-1"
                aria-hidden="true"
              />
            </div>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features that help you create better content, build more authority, and track real results—all automated so you can focus on growing your business.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 mb-12 sm:mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-background rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-border/40 hover:border-border/60 transition-all duration-300 hover:shadow-md"
              >
                <div className="flex flex-col items-start">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary/10 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-5 text-primary">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3 leading-tight text-left">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed text-left">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <Button
              onClick={handleClick}
              disabled={signingIn}
              size="lg"
              className="w-full sm:w-auto rounded-full text-lg px-8 py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg min-h-[60px] relative overflow-hidden group mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
              <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdditionalFeatures;
